import { useMemo } from 'react'
import { createFileRoute, getRouteApi, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, FileDown, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@workspace/ui/components/button'
import {
  useCreateReportExport,
  useReportPackPreview,
} from '@workspace/react-query'
import type { ReportBuilderPreviewInput } from '@workspace/utils/types'
import { reportsDashboardFiltersSchema } from '@workspace/utils/schemas/reports-dashboard'
import { requireActiveSubscription, requireRoles } from '@/lib/middleware'
import { authClient } from '@/lib/auth'
import { Main } from '@/components/layout/main'
import { ReportPackView } from '@/features/reports/builder/report-pack-view'

/** The builder selection, carried through the URL so the preview is refreshable. */
const previewSearchSchema = z.object({
  mode: z.enum(['single', 'selected', 'all']).catch('selected'),
  participants: z.array(z.string()).catch([]),
  sections: z.array(z.string()).catch([]),
  filters: reportsDashboardFiltersSchema.optional().catch(undefined),
})

const route = getRouteApi('/_authenticated/_organization/reports/preview')

type PreviewFormat = 'pdf' | 'docx' | 'xlsx'
const EXPORT_FORMATS: { value: PreviewFormat; label: string }[] = [
  { value: 'pdf', label: 'PDF' },
  { value: 'docx', label: 'Word' },
  { value: 'xlsx', label: 'Excel' },
]

function ReportPreviewPage() {
  const { data: activeOrganization, isPending: isLoadingOrganization } =
    authClient.useActiveOrganization()
  const search = route.useSearch()
  const navigate = useNavigate()
  const organizationId = activeOrganization?.id

  const input: ReportBuilderPreviewInput = useMemo(
    () => ({
      mode: search.mode,
      participantIds: search.participants,
      sections: search.sections,
      filters: search.filters,
    }),
    [search],
  )

  const { data, isLoading, error } = useReportPackPreview({
    organizationId,
    input,
    enabled: !!organizationId,
  })
  const createExport = useCreateReportExport({
    organizationId: organizationId ?? '',
  })

  const result = data?.data
  const pack = result?.pack

  const onExport = async (format: PreviewFormat) => {
    try {
      await createExport.mutateAsync({
        mode: input.mode,
        format,
        output: 'combined',
        participantIds:
          input.mode === 'all' ? undefined : input.participantIds,
        sections: input.sections,
        filters: input.filters,
      })
      toast.success(
        "Export queued — you'll be notified when it's ready to download.",
      )
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to queue export',
      )
    }
  }

  return (
    <Main className='flex flex-1 flex-col gap-6'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
        <div className='flex items-start gap-3'>
          <Button
            variant='outline'
            size='icon'
            onClick={() => navigate({ to: '/reports' })}
            aria-label='Back to reports'
          >
            <ArrowLeft className='h-4 w-4' />
          </Button>
          <div>
            <p className='text-primary text-xs font-semibold uppercase tracking-wider'>
              Reports · Preview
            </p>
            <h1 className='mt-1 text-2xl font-bold tracking-tight'>
              Report preview
            </h1>
            <p className='text-muted-foreground mt-1 text-sm'>
              Live preview of the report pack — the same charts and tables your
              export contains.
            </p>
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {EXPORT_FORMATS.map((f) => (
            <Button
              key={f.value}
              variant={f.value === 'pdf' ? 'default' : 'outline'}
              onClick={() => onExport(f.value)}
              disabled={createExport.isPending || !pack}
              className={
                f.value === 'pdf'
                  ? 'bg-[#11302c] text-white hover:bg-[#0c2420]'
                  : undefined
              }
            >
              {createExport.isPending ? (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              ) : (
                <FileDown className='mr-2 h-4 w-4' />
              )}
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {isLoadingOrganization || isLoading ? (
        <div className='space-y-4'>
          <div className='bg-muted h-32 animate-pulse rounded-2xl' />
          <div className='bg-muted h-64 animate-pulse rounded-2xl' />
        </div>
      ) : error || data?.error ? (
        <p className='rounded-lg border border-dashed p-8 text-center text-sm text-[#c0492f]'>
          {data?.error?.message ??
            (error instanceof Error
              ? error.message
              : 'Failed to load the preview.')}
        </p>
      ) : pack ? (
        <>
          {result?.truncated && (
            <p className='rounded-lg border border-[#c98a2b]/40 bg-[#f7ecd6]/40 px-4 py-2.5 text-sm text-[#a9701c]'>
              Showing {result.rendered} of {result.totalInScope} participants in
              this preview. The export includes all {result.totalInScope}.
            </p>
          )}
          <ReportPackView pack={pack} />
        </>
      ) : (
        <p className='text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm'>
          Nothing to preview. Choose participants and sections in the builder
          first.
        </p>
      )}
    </Main>
  )
}

export const Route = createFileRoute(
  '/_authenticated/_organization/reports/preview',
)({
  validateSearch: previewSearchSchema,
  beforeLoad: async () => {
    // Mirror the dashboard gate: owner/admin only + active subscription.
    await requireRoles(['owner', 'admin'])
    await requireActiveSubscription()
  },
  component: ReportPreviewPage,
})
